import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigContext } from './utils/Config';
import {
  Box, FormControl, FormLabel, Input, Button, Alert, AlertIcon, AlertTitle, AlertDescription, Text,
  Spinner, Flex, Tabs, TabList, TabPanels, Tab, TabPanel, Select, Textarea, 
} from '@chakra-ui/react';
import fetchData from './utils/Util';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PhoneInput, { getCountries } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags'; // Import the flags module
import AddressForm from './utils/AddressForm';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import ChangePassword from './utils/ChangePassword';

const MyAccount = ({ showChangePasswordTab }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const [country, setCountry] = useState('US'); // Define country state
  const [formattedMobile, setFormattedMobile] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('US'); // Define phone country state
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState(''); // Define formatted phone number state
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [namePrefixes, setNamePrefixes] = useState([]);
  const [isAddingNewAddress, setIsAddingNewAddress] = useState(false);
  const [addressTabIndex, setAddressTabIndex] = useState(0);
  const [isChangingDefaultAddress, setIsChangingDefaultAddress] = useState(false);

  const config = useContext(ConfigContext);

  const [boUserData, setBoUserData] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    name_prefix: '',
    email: '',
    phone: '',
    mobile: '',
    company: '',
    client_website: '',
    birthdate: '',
    // subscribe_to_newsletter: '',
    // interested_in: '',
    website_language: '',
    shipping_instructions_client: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const handlePhoneChange = useCallback((value, data, field) => {
    const countryCode = data?.countryCode || '';
    const phoneNumber = value ? `${countryCode}${value}` : '';
    const formattedNumber = value ? formatPhoneNumberIntl(phoneNumber) : '';
  
    if (field === 'phone') {
      setFormattedPhoneNumber(formattedNumber);
      setPhoneCountry(data?.countryCode || phoneCountry);
    } else if (field === 'mobile') {
      setFormattedMobile(formattedNumber);
      setCountry(data?.countryCode || country);
    }
  
    setFormData((prevFormData) => ({
      ...prevFormData,
      [field]: value,
    }));
  }, [country, phoneCountry]);

  const hasSavedAddresses = () => {
    return !isLoading && addresses.some(address => address.id);
  };

  // Fet bo user.
  useEffect(() => {
    /**
     * Get datatable-clients from the backoffice API.
     */
    const fetchBoUserData = async () => {
      try {
        if (!config.userData || !config.userData.uuid) {
          // Wait until data is fully loaded.
          return;
        }
        if (!config.userData.bo_access_token) {
          setError(t('myAccount.connectionFailed'));
          return;
        }
        const response = await fetchData(
          `${config.siteData.backoffice_url}/api/v1.0/datatable-clients?filter[uuid][value]=${config.userData.uuid}&access_token=${config.userData.bo_access_token}`,
          { mockData: 'api-me' }
        );
        if (!response.ok) {
          throw new Error(response.title || t('myAccount.networkError'));
        }
        const result = await response.json();
        const data = result.data[0];
        console.log('User data:', data);
        setBoUserData(data);
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          name_prefix: data.name_prefix?.id || '',
          email: data.email || '',
          phone: data.phone || '',
          mobile: data.mobile || '',
          company: data.company || '',
          client_website: data.client_website || '',
          birthdate: data.birthdate ? new Date(data.birthdate).getTime() : null,
          // subscribe_to_newsletter: data.subscribe_to_newsletter || '',
          // interested_in: data.interested_in || '',
          website_language: data.website_language || '',
          shipping_instructions_client: data.shipping_instructions_client || '',
        });
      } catch (error) {
        setError(error.message);
      }
    };

    const fetchDataAsync = async () => {
      await fetchBoUserData();
   };

    fetchDataAsync().catch((error) => {
      setError(error.message);
      setIsLoading(false);
    });
  }, [config, t]);

  useEffect(() => {
    const detectCountry = () => {
      const browserLanguage = navigator.language || navigator.userLanguage;
      const [languageCode, countryCode] = browserLanguage.split('-');
      
      if (countryCode) {
        return countryCode.toUpperCase();
      } else {
        const countries = getCountries();
        const matchingCountry = countries.find(country => country.toLowerCase() === languageCode.toLowerCase());
        return matchingCountry || 'US';
      }
    };
  
    const detectedCountry = detectCountry();
    setCountry(detectedCountry);
    setPhoneCountry(detectedCountry);
  }, []);

  useEffect(() => {
    console.log('Current country:', country);
    console.log('Current phone country:', phoneCountry);
  }, [country, phoneCountry]);

  // Fetch prefix.
  useEffect(() => {

    const fetchNamePrefixes = async () => {
      if (!config || !config.userData || !config.userData.bo_access_token) return;
      try {
        const response = await fetchData(
          `${config.siteData.backoffice_url}/api/v1.0/name_prefixes?access_token=${config.userData.bo_access_token}`,
          { mockData: 'api-name-prefixes' }
        );
        if (!response.ok) {
          throw new Error(response.title || 'Network response was not ok for name prefixes');
        }
        const data = await response.json();
        setNamePrefixes(data.data);
      }
      catch (error) {
        setError(error.message);
      }
    };

   fetchNamePrefixes().catch((error) => {
      setError(error.message);
      setIsLoading(false);
    });
  }, [config, namePrefixes.length, config.userData?.bo_access_token, config.siteData]);

  // Fetch address.
  useEffect(() => {

    const fetchAddresses = async () => {
      try {
        if (!boUserData || !config.userData.bo_access_token) return;
        const response = await fetchData(
          `${config.siteData.backoffice_url}/api/client-address?filter[client]=${boUserData.id}&access_token=${config.userData.bo_access_token}`,
          { mockData: 'api-client-address' }
        );
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.title || 'Network response was not ok for addresses');
        }
        const data = await response.json();
        setAddresses(data.data);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchAddresses().catch((error) => {
      setError(error.message);
      setIsLoading(false);
    });

    setIsLoading(false);

  }, [config, boUserData, config.userData.bo_access_token]);

  useEffect(() => {
    if (isChangingDefaultAddress) {
      return; 
    }
    if (isAddingNewAddress || addresses.length === 0) {
      setActiveTab(2); // Set to address tab if adding new address or no addresses
    } else if (showChangePasswordTab) {
      setActiveTab(1); // Set to change password tab if requested
    } else {
      setActiveTab(0); // Set to first tab (personal info) by default
    }
  }, [addresses, showChangePasswordTab, isAddingNewAddress]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Check email validity
    if (e.target.name === 'email') {
      validateEmail(e.target.value); // Add this line
    }
  };

  // Add function to validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsValidEmail(emailRegex.test(email));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage('...');
    setError('');

    if (!isValidEmail) {
      setError('Please enter a valid email address.');
      return;
    }

    // Update phone/ date to formatted values.
    const updatedFormData = {
      ...formData,
      mobile: formattedMobile ? formattedMobile : formData.mobile,
      phone: formattedPhoneNumber ? formattedPhoneNumber : formData.phone,
    };

    setFormData(updatedFormData);

    fetch(`${config.siteData.backoffice_url}/api/datatable-clients/${boUserData.id}?access_token=${config.userData.bo_access_token}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedFormData),
    })
      .then((response) => {

        if (!response.ok) {
          response.json().then((res) => setError(res.title || 'Network error when updating account'));
        }
        return response.json();
      })
      .then((data) => {
        setSuccessMessage('Your account details have been updated');
        setError('');
      })
      .catch((error) => {
        setError(error.message);
      });
  };
  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      birthdate: date ? date.getTime() : null, // Convert to timestamp
    });
  };

  const handleAddNewAddress = () => {
    const newAddress = {
      default_address: false,
      address_types: [],
      address: {
        name_prefix: '',
        first_name: '',
        care_of: '',
        last_name: '',
        organisation_name: '',
        thoroughfare: '',
        premise: '',
        locality: '',
        administrative_area: '',
        postal_code: '',
        country: '',
      },
    };
    setAddresses([...addresses, newAddress]);
    setIsAddingNewAddress(true);
    setActiveTab(2); 
    setAddressTabIndex(addresses.length);
  };

  const handleDeleteAddress = async (index) => {
    try {
      const addressId = addresses[index].id;
      const url = `${config.siteData.backoffice_url}/api/client-address/${addressId}?sale_id=4&access_token=${config.userData.bo_access_token}`;

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'text/plain;charset=UTF-8',
        },
        body: JSON.stringify({ published: 0 }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const updatedAddresses = addresses.filter((_, idx) => idx !== index);
      setAddresses(updatedAddresses);
      setSuccessMessage('Address deleted successfully');
    } catch (error) {
      setError('An error occurred while deleting the address. Please try again.');
    }
  };

  const handleSaveAddress = async (index, data) => {
    try {
      const addressData = {
        client: boUserData.id,
        default_address: data.defaultAddress,
        address_types: data.addressTypes,
        address: {
          name_prefix: Number(data.namePrefix),
          care_of: data.careOf,
          first_name: data.firstName,
          last_name: data.lastName,
          organisation_name: data.company,
          thoroughfare: data.address1,
          premise: data.address2,
          locality: data.city,
          administrative_area: data.province,
          postal_code: data.zip,
          country: data.country,
        },
      };

      const isUpdate = !!addresses[index].id; // Check if there's an address id
      const url = `${config.siteData.backoffice_url}/api/client-address${isUpdate ? `/${addresses[index].id}` : ''}?access_token=${config.userData.bo_access_token}`;

      const response = await fetchData(url, {
        method: isUpdate ? 'PATCH' : 'POST', // Use PATCH if updating, otherwise POST
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addressData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.title || 'Network response was not ok for saving address');
      } else {
        const responce = await response.json();
        const updatedAddress = responce.data[0];
        const updatedAddresses = [...addresses];
        updatedAddresses[index] = updatedAddress;

        // If the saved address is set as default, update all other addresses to not be default
        if (data.defaultAddress) {
          const updatedAddressesWithDefaultReset = updatedAddresses.map((address, idx) => {
            if (idx !== index) {
              return { ...address, default_address: false };
            }
            return address;
          });
          setAddresses(updatedAddressesWithDefaultReset);
        } else {
          setAddresses(updatedAddresses);
        }

        setSuccessMessage('Address saved successfully');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleAddressChange = (index, data) => {
    const currentAddressTabIndex = addressTabIndex;   

    if ('default_address' in data) {
      setIsChangingDefaultAddress(true);
    }

    const updatedAddresses = [...addresses];
    updatedAddresses[index] = { ...updatedAddresses[index], ...data };
    setAddresses(updatedAddresses);
    if ('default_address' in data && data.default_address) {
      const addressesWithUpdatedDefaults = updatedAddresses.map((address, idx) => {
        if (idx !== index) {
          return { ...address, default_address: false };
        }
        return address;
      });
      setAddresses(addressesWithUpdatedDefaults);
    }
    setAddressTabIndex(currentAddressTabIndex);

    if ('default_address' in data) {
      setTimeout(() => setIsChangingDefaultAddress(false), 0);
    }
  };

  if (isLoading || !config.userData || !config.siteData) {
    return (
      <Flex width="100vw" height="100vh" justifyContent="center" alignItems="center">
        <Spinner color="blue.500" size="xl" thickness="4px"/>
      </Flex>
    );
  }

  if (config.success === false) {
    return (
    <Alert status="error" marginBottom="20px">
      <AlertIcon />
      Cannot access site settings.
    </Alert>);
  }

  return (
    <Box className="account">
      <h1>{t('myAccount.title')}</h1>
      {!isLoading && !hasSavedAddresses() && (
        <Alert status="warning" marginBottom="20px">
          <AlertIcon />
          {t('myAccount.noAddressesMessage')}
        </Alert>
      )}
      <Tabs index={activeTab} onChange={(index) => setActiveTab(index)}>
      <TabList 
        flexDirection={["column", "row"]} 
        alignItems="flex-start"
        borderBottomWidth={["0", "1px"]}
      >
        <Tab width={["100%", "auto"]} justifyContent="flex-start" py={2}>
          {t('myAccount.tabs.personalInfo')}
        </Tab>
        <Tab width={["100%", "auto"]} justifyContent="flex-start" py={2}>
          {t('myAccount.tabs.changePassword')}
        </Tab>
        <Tab width={["100%", "auto"]} justifyContent="flex-start" py={2}>
          {t('myAccount.tabs.addresses')}
        </Tab>
      </TabList>

        <TabPanels>
          <TabPanel>
            <form onSubmit={handleSubmit}>
              <Select
                name="name_prefix"
                value={formData.name_prefix}
                onChange={handleChange}
                width="auto"
                className="name-prefix"
              >
                <option value="">{t('myAccount.selectTitle')}</option>
                {namePrefixes.map((prefix) => (
                  <option key={prefix.id} value={prefix.id}>
                    {prefix.label}
                  </option>
                ))}
              </Select>

              <FormControl marginBottom="20px" isRequired>
                <FormLabel>{t('myAccount.firstName')}</FormLabel>
                <Input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder={t('myAccount.enterFirstName')}
                  required
                  className="first-name"
                />
              </FormControl>
              <FormControl marginBottom="20px" isRequired>
                <FormLabel>{t('myAccount.lastName')}</FormLabel>
                <Input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder={t('myAccount.enterLastName')}
                  required
                  className="last-name"
                />
              </FormControl>
              <FormControl marginBottom="20px" isRequired>
                <FormLabel>{t('myAccount.email')}</FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('myAccount.enterEmail')}
                  required
                  className="email"
                />
              </FormControl>
              <FormControl marginBottom="20px">
                <FormLabel className="phone-number-label">
                  {t('myAccount.mobileNumber')}
                </FormLabel>
                <PhoneInput
                  className="phone-input-dropdown"
                  country={country}
                  value={formData.mobile}
                  onChange={(value, data) => handlePhoneChange(value, data, 'mobile')}
                  placeholder={t('myAccount.enterMobileNumber')}
                  onCountryChange={(countryCode) => setCountry(countryCode)}
                  // inputStyle={{ maxWidth: 'calc(100% - 40px)', flex: '1' }}
                  // containerStyle={{ display: 'flex', alignItems: 'center' }}
                  flags={flags}
                  // inputProps={{
                  //   style: { maxWidth: '100%' },
                  // }}
                  // flagSize={40}
                  defaultCountry={country}                 
                />
              </FormControl>
              <Box mt={4}>
                {/* Display formatted international phone number */}
                {formattedMobile && (
                  <Text marginBottom="30px" fontSize="lg">
                    Standard International Format: {formattedMobile}
                  </Text>
                )}
                {
                  //console.log("Mobile Number:", rawMobileNumber)} {/* Add this line */
                }
              </Box>
              <FormControl marginBottom="20px">
                <FormLabel className="phone-number-label">{t('myAccount.phoneNumber')}</FormLabel>
                <PhoneInput
                  className="phone-input-dropdown"
                  country={phoneCountry}
                  value={formData.phone}
                  onChange={(value, data) => handlePhoneChange(value, data, 'phone')}
                  placeholder={t('myAccount.enterPhoneNumber')}
                  onCountryChange={(countryCode) => setPhoneCountry(countryCode)}
                  flags={flags}
                  defaultCountry={phoneCountry}
                />
              </FormControl>
              <Box mt={4}>
                {formattedPhoneNumber && (
                  <Text marginBottom="30px" fontSize="lg">
                    {t('myAccount.standardInternationalFormat')}: {formattedPhoneNumber}
                  </Text>
                )}
                <Text color="gray.500" fontSize="sm" marginBottom="20px">
                  {t('myAccount.phoneNumberInstructions')}
                </Text>
              </Box>

              {/* Add Company field */}
              <FormControl marginBottom="20px">
                <FormLabel>{t('myAccount.company')}</FormLabel>
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder={t('myAccount.enterCompany')}
                  className="company"
                />
              </FormControl>
              <FormControl marginBottom="20px">
                <FormLabel>{t('myAccount.website')}</FormLabel>
                <Input
                  type="url"
                  name="client_website"
                  value={formData.client_website}
                  onChange={handleChange}
                  placeholder={t('myAccount.enterWebsite')}
                  pattern="https?://.*"
                  title={t('myAccount.validUrlMessage')}
                  className="website"
                />
              </FormControl>
              {/* Date picker field */}
              <FormControl marginBottom="20px">
                <FormLabel>{t('myAccount.birthDate')}</FormLabel>
                <DatePicker
                  selected={formData.birthdate ? new Date(formData.birthdate) : null}
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText={t('myAccount.selectBirthDate')}
                  className="birthday"
                />
              </FormControl>
              <FormControl marginBottom="20px">
                <FormLabel>{t('myAccount.language')}</FormLabel>
                <Select
                  name="website_language"
                  value={formData.website_language}
                  onChange={handleChange}
                  width="auto"
                >
                  <option value="">{t('myAccount.selectLanguage')}</option>
                  {config.siteData.languages.map((lang) => (
                    <option key={lang} value={lang}>
                      {t(`myAccount.languages.${lang}`)}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {/*<FormControl marginBottom="20px">*/}
              {/*  <Checkbox*/}
              {/*    name="subscribe_to_newsletter"*/}
              {/*    isChecked={formData.subscribe_to_newsletter}*/}
              {/*    onChange={handleCheckboxChange}*/}
              {/*  >*/}
              {/*    Subscribe to our newsletter*/}
              {/*  </Checkbox>*/}
              {/*</FormControl>*/}
              <FormControl marginBottom="20px">
                <FormLabel>{t('myAccount.shippingInstructions')}</FormLabel>
                <Textarea
                  name="shipping_instructions_client"
                  value={formData.shipping_instructions_client}
                  onChange={handleChange}
                  placeholder={t('myAccount.enterShippingInstructions')}
                  className="shipping-instructions"
                />
              </FormControl>


              <Text className="help-text" dangerouslySetInnerHTML={{ __html: t('register.requiredFields', { asterisk: '<span style="color: red;">*</span>' }) }} />


              <Button type="submit" colorScheme="blue">{t('myAccount.updateAccount')}</Button>
            </form>
          </TabPanel>
           Reset password
          <TabPanel>
            <ChangePassword />
          </TabPanel>
           Addresses
          <TabPanel>
            <Button onClick={handleAddNewAddress} colorScheme="blue" marginBottom="20px">
              {t('myAccount.addNewAddress')}
            </Button>

            <Tabs index={addressTabIndex} onChange={(index) => setAddressTabIndex(index)}>
              <TabList 
                flexDirection={["column", "row"]} 
                alignItems="flex-start"
                borderBottomWidth={["0", "1px"]}
              >
                {addresses.filter(address => address.address).map((address, index) => (
                  <Tab 
                    key={index}
                    width={["100%", "auto"]} 
                    justifyContent="flex-start" 
                    py={2}
                  >Address {index + 1}</Tab>
                ))}
              </TabList>
              <TabPanels>
                {addresses.filter(address => address.address).map((address, index) => (
                  <TabPanel key={index}>
                    <AddressForm
                      address={address}
                      onSave={(data) => handleSaveAddress(index, data)}
                      onDelete={() => handleDeleteAddress(index)}
                      namePrefixes={namePrefixes}
                      isDefault={address.default_address}
                      onDefaultChange={(value) => handleAddressChange(index, { default_address: value })}
                      defaultCountry={country}
                      isFirstAddress={addresses.length === 0 || !addresses.some(addr => addr.id)}
                    />
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </TabPanel>
        </TabPanels>
      </Tabs>
      {successMessage && (
        <Alert status="success" marginBottom="20px">
          <AlertIcon />
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert status="error" marginBottom="20px">
          <AlertIcon />
          <AlertTitle>{t('myAccount.error')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </Box>
  );
};

export default MyAccount;
